/**
 * Shared create/edit user dialog body.
 *
 * Both UsersPage and UserManager render this component for the inner
 * create/edit form.  Differences between the two callers are passed as props:
 *
 *  - `companySectionSlot`  — renders the "Company & Role" section (or just
 *                            "Role" in UserManager).  Placed between Basic
 *                            Information and the RBAC fields.
 *  - `subCompanyAdminHintText` — hint under the Sub-Company select when the
 *                            role is SubCompanyAdmin (the two callers use
 *                            different labels).
 */
import React from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Grid,
} from '@mui/material';
import { Person } from '@mui/icons-material';
import { User, AccountType } from '../../types/auth';
import { Branch, SubCustomer } from '../../types/company';
import { CreateUserData } from './userFormShared';
import SubCompanySelect from './SubCompanySelect';
import type { SubCompany } from '../../types/company';

/**
 * Renders the role <MenuItem> options that are common to the user-form Role
 * selects (User / Licensee Admin / CarbonCare Admin / Master Admin), gated by
 * the current user's account type.
 */
// Returns an ARRAY (not a Fragment): MUI <Select> enumerates children via
// React.Children, which flattens arrays but does not descend into Fragments —
// items wrapped in a Fragment would silently fail to render in the dropdown.
export const renderElevatedRoleMenuItems = (currentAccountType?: AccountType) => [
  currentAccountType! >= AccountType.User && (
    <MenuItem key="user" value={AccountType.User}>User</MenuItem>
  ),
  currentAccountType! >= AccountType.LicenseeAdmin && (
    <MenuItem key="licensee-admin" value={AccountType.LicenseeAdmin}>Licensee Admin</MenuItem>
  ),
  currentAccountType! >= AccountType.CarbonCareAdmin && (
    <MenuItem key="carbon-care-admin" value={AccountType.CarbonCareAdmin}>CarbonCare Admin</MenuItem>
  ),
  currentAccountType === AccountType.MasterAdmin && (
    <MenuItem key="master-admin" value={AccountType.MasterAdmin}>Master Admin</MenuItem>
  ),
].filter(Boolean);

export interface UserFormDialogProps {
  open: boolean;
  editingUser: User | null;
  formData: CreateUserData;
  validationErrors: Record<string, string>;
  subCompanies: SubCompany[];
  branches: Branch[];
  subCustomers: SubCustomer[];
  selectedSubCompanyId: number | null;
  selectedBranchId: number | null;
  selectedSubCustomerId: number | null;
  loadingOrgData: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  onInputChange: (field: string, value: any) => void;
  onSubCompanyChange: (subCompanyId: number, code: string) => void;
  onBranchChange: (branchId: number, code: string) => void;
  onSubCustomerChange: (subCustomerId: number) => void;
  /** Slot for the "Company & Role" / "Role" section between Basic Info and RBAC */
  companySectionSlot: React.ReactNode;
  /** Hint text shown under the Sub-Company select for SubCompanyAdmin role */
  subCompanyAdminHintText?: string;
}

const UserFormDialog: React.FC<UserFormDialogProps> = ({
  open,
  editingUser,
  formData,
  validationErrors,
  subCompanies,
  branches,
  subCustomers,
  selectedSubCompanyId,
  selectedBranchId,
  selectedSubCustomerId,
  loadingOrgData,
  onCancel,
  onSubmit,
  onInputChange,
  onSubCompanyChange,
  onBranchChange,
  onSubCustomerChange,
  companySectionSlot,
  subCompanyAdminHintText,
}) => (
  <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
    <DialogTitle>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Person />
        {editingUser ? 'Edit User' : 'Create New User'}
      </Box>
    </DialogTitle>
    <DialogContent sx={{ minHeight: 400 }}>
      <Box sx={{ mt: 2 }}>
        {/* Basic Information */}
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          Basic Information
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First Name"
              value={formData.firstName}
              onChange={(e) => onInputChange('firstName', e.target.value)}
              error={!!validationErrors.firstName}
              helperText={validationErrors.firstName}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last Name"
              value={formData.lastName}
              onChange={(e) => onInputChange('lastName', e.target.value)}
              error={!!validationErrors.lastName}
              helperText={validationErrors.lastName}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => onInputChange('email', e.target.value)}
              error={!!validationErrors.email}
              helperText={validationErrors.email}
              required
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Company & Role section — caller-supplied */}
        {companySectionSlot}

        <Divider sx={{ my: 3 }} />

        {/* Role-Specific RBAC Fields */}
        {/* <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
          Position/Branch
        </Typography> */}
        <Grid container spacing={2} sx={{ mb: 3 }}>

          {/* SubCompanyAdmin: needs a sub-company */}
          {formData.accountType === AccountType.SubCompanyAdmin && (
            <Grid item xs={12}>
              <SubCompanySelect
                subCompanies={subCompanies}
                selectedSubCompanyId={selectedSubCompanyId}
                loadingOrgData={loadingOrgData}
                validationError={validationErrors.subCompany}
                hintText={subCompanyAdminHintText}
                onChange={onSubCompanyChange}
              />
            </Grid>
          )}

          {/* BranchAdmin: needs sub-company then branch */}
          {formData.accountType === AccountType.BranchAdmin && (
            <>
              <Grid item xs={12}>
                <SubCompanySelect
                  subCompanies={subCompanies}
                  selectedSubCompanyId={selectedSubCompanyId}
                  loadingOrgData={loadingOrgData}
                  validationError={validationErrors.subCompany}
                  onChange={onSubCompanyChange}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required error={!!validationErrors.branch}>
                  <InputLabel>Branch</InputLabel>
                  <Select
                    value={selectedBranchId || ''}
                    onChange={(e) => {
                      const branchId = e.target.value as number;
                      const branch = branches.find((b) => b.id === branchId);
                      onBranchChange(branchId, branch?.code || '');
                    }}
                    label="Branch"
                    disabled={!selectedSubCompanyId || loadingOrgData}
                  >
                    {!selectedSubCompanyId && (
                      <MenuItem value="" disabled>
                        Select a sub-company first
                      </MenuItem>
                    )}
                    {selectedSubCompanyId &&
                      branches
                        .filter(
                          (b) =>
                            (b.subCompanyId || b.sub_company_id) === selectedSubCompanyId,
                        )
                        .length === 0 && (
                        <MenuItem value="" disabled>
                          No branches available for this sub-company
                        </MenuItem>
                      )}
                    {selectedSubCompanyId &&
                      branches
                        .filter(
                          (b) =>
                            (b.subCompanyId || b.sub_company_id) === selectedSubCompanyId,
                        )
                        .map((branch) => (
                          <MenuItem key={branch.id} value={branch.id}>
                            {branch.name} {branch.code ? `(${branch.code})` : ''}
                          </MenuItem>
                        ))}
                  </Select>
                  {validationErrors.branch && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {validationErrors.branch}
                    </Typography>
                  )}
                  {!validationErrors.branch && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                      Required for Branch Admin role
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            </>
          )}

          {/* SubCustomerAdmin: needs a sub-customer */}
          {formData.accountType === AccountType.SubCustomerAdmin && (
            <Grid item xs={12}>
              <FormControl fullWidth required error={!!validationErrors.subCustomerId}>
                <InputLabel>Sub-Customer</InputLabel>
                <Select
                  value={selectedSubCustomerId || ''}
                  onChange={(e) => {
                    onSubCustomerChange(e.target.value as number);
                  }}
                  label="Sub-Customer"
                  disabled={loadingOrgData || subCustomers.length === 0}
                >
                  {subCustomers.length === 0 && (
                    <MenuItem value="" disabled>
                      No sub-customers available
                    </MenuItem>
                  )}
                  {subCustomers.map((sc) => (
                    <MenuItem key={sc.id} value={sc.id}>
                      {sc.name} {sc.code ? `(${sc.code})` : ''}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.subCustomerId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {validationErrors.subCustomerId}
                  </Typography>
                )}
                {!validationErrors.subCustomerId && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                    Required for SubCustomer Admin role
                  </Typography>
                )}
              </FormControl>
            </Grid>
          )}
        </Grid>

        {/* Created / Last Updated footer — only shown when editing */}
        {editingUser && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Created:</strong>{' '}
              {new Date(editingUser.createdAt).toLocaleString()}
              <br />
              <strong>Last Updated:</strong>{' '}
              {new Date(editingUser.updatedAt).toLocaleString()}
            </Typography>
          </Box>
        )}
      </Box>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 3 }}>
      <Button onClick={onCancel} size="large" variant="outlined">
        Cancel
      </Button>
      <Button onClick={onSubmit} variant="contained" size="large">
        {editingUser ? 'Update User' : 'Create User'}
      </Button>
    </DialogActions>
  </Dialog>
);

export default UserFormDialog;
